import { useState, useRef, useEffect } from 'react';
import { Search, Loader2, ChevronDown, Phone, UserSearch } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { searchPatients } from '@/lib/api/users';
import { HealoChannelGlyph } from '@/components/messages/healo-channel-glyph';
import { cn } from '@/lib/utils';

/**
 * Debounced patient lookup dropdown. Calls `searchPatients` and renders matches with expandable
 * phone selection. `onSelectPatient` receives the chosen patient; when a specific phone is picked
 * ("Use this") the patient carries a `_selectedPhone` field. Shared by the Search page and the
 * "New message" compose dialog so the two never diverge.
 */
export function PatientSearchDropdown({ onSelectPatient, label = 'Find Patient', placeholder = 'Search by name, phone…', autoFocus = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await searchPatients({ searchTerm: val.trim() });
        setResults(data.items);
        setIsOpen(true);
        setExpandedId(null);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 350);
  };

  const handleSelectPatient = (patient) => {
    onSelectPatient(patient);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setExpandedId(null);
  };

  const getPrimaryPhone = (patient) => {
    const phones = patient.contacts?.Phones;
    if (Array.isArray(phones) && phones.length > 0) {
      const verified = phones.find((p) => p.Verified);
      return (verified || phones[0])?.E164 || '';
    }
    return '';
  };

  return (
    <div ref={containerRef} className="relative space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <UserSearch className="h-3 w-3" /> {label}
      </label>
      <div className="relative">
        <Input
          id="patient-search-input"
          placeholder={placeholder}
          value={query}
          onChange={handleQueryChange}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          autoComplete="off"
          autoFocus={autoFocus}
          className="pr-8"
        />
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          {isLoading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Search className="h-3.5 w-3.5 opacity-50" />}
        </div>
      </div>

      {isOpen && results.length > 0 && (
        <ScrollArea
          viewportClassName="max-h-72"
          className="absolute z-50 left-0 right-0 top-full mt-1 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl"
        >
          {results.map((patient) => {
            const name = patient.displayName
              || `${patient.profile?.FirstName || ''} ${patient.profile?.LastName || ''}`.trim()
              || 'Unknown Patient';
            const primaryPhone = getPrimaryPhone(patient);
            const isExpanded = expandedId === patient.id;
            const phones = patient.contacts?.Phones || [];

            return (
              <div key={patient.id} className="border-b border-border/50 last:border-b-0">
                <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-muted/60 transition-colors">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-bold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300">
                    {name.split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('')}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="flex min-w-0 items-center gap-1.5 truncate text-sm font-medium text-foreground">
                      <span className="min-w-0 truncate">{name}</span>
                      {patient.appConnected && (
                        // The patient has signed in from the Healo app at least once — an
                        // app-channel message will reach them there.
                        <span className="inline-flex shrink-0" title="Uses the Healo app">
                          <HealoChannelGlyph className="h-3 w-3 shrink-0" />
                        </span>
                      )}
                    </span>
                    {primaryPhone && (
                      <span className="font-mono text-[10px] text-muted-foreground">{primaryPhone}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : patient.id)}
                    className="ml-1 rounded p-0.5 hover:bg-muted transition-colors"
                    title="Show details"
                  >
                    <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', isExpanded && 'rotate-180')} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectPatient(patient)}
                    className="ml-1 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap"
                  >
                    Select
                  </button>
                </div>

                {isExpanded && (
                  <div className="border-t border-border/40 bg-muted/30 px-4 pb-3 pt-2 space-y-2 text-xs">
                    {phones.length > 0 && (
                      <div className="space-y-1">
                        <span className="font-semibold uppercase text-muted-foreground flex items-center gap-1">
                          <Phone className="h-2.5 w-2.5" /> Phones
                        </span>
                        {phones.map((p, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="font-mono text-foreground">{p.E164}</span>
                            <div className="flex items-center gap-1.5">
                              {p.Verified && (
                                <Badge className="border-none bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 text-[9px]">Verified</Badge>
                              )}
                              <button
                                type="button"
                                onClick={() => handleSelectPatient({ ...patient, _selectedPhone: p.E164 })}
                                className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 hover:bg-amber-500/20 dark:text-amber-300 transition-colors"
                              >
                                Use this
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {(patient.profile?.DOB || patient.profile?.Sex) && (
                      <div className="flex items-center gap-4 text-muted-foreground">
                        {patient.profile?.DOB && <span><span className="font-medium text-foreground">DOB:</span> {patient.profile.DOB.slice(0, 10)}</span>}
                        {patient.profile?.Sex && <span><span className="font-medium text-foreground">Sex:</span> {patient.profile.Sex}</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </ScrollArea>
      )}

      {isOpen && !isLoading && results.length === 0 && query.trim() && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-xl border border-border bg-popover px-4 py-3 text-center text-xs text-muted-foreground shadow-xl">
          No patients found for &ldquo;{query}&rdquo;
        </div>
      )}
    </div>
  );
}
